
(function () {
  'use strict';

  if (window.ProductBuilderCartValidation) return;

  const guards = new Set();

  const state = {
    pending: 0,
    refreshing: 0,
    checking: false,
    failed: false,
    version: 0,
    sequence: 0,
    model: null
  };

  const renderAll = () =>
    guards.forEach((guard) => guard.render());

  const missingSteps = (model) =>
    window.ProductBuilderRules.getMissingSteps(
      model.items,
      model.requirements
    );

  const isEmpty = (model) =>
    !model.items.some((item) => Number(item.quantity) > 0);

  function parseModel(value) {
    const model = JSON.parse(value);

    if (
      !Array.isArray(model.items) ||
      !Array.isArray(model.requirements) ||
      model.requirements.some(
        (requirement) =>
          !Array.isArray(requirement.steps) ||
          requirement.steps.some(
            (step) => !Array.isArray(step.productIds)
          )
      )
    ) {
      throw new Error('Invalid cart validation data');
    }

    return model;
  }

  async function refresh() {
    const sequence = ++state.sequence;

    state.refreshing++;
    state.failed = false;
    renderAll();

    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      10000
    );

    try {
      const url = new URL(
        theme.routes.cart,
        window.location.origin
      );

      url.searchParams.set(
        'section_id',
        'product-builder-cart-validation'
      );

      const response = await fetch(url, {
        cache: 'no-store',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Cart validation section unavailable');
      }

      const html = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

      const payload = html.querySelector(
        '[data-builder-cart-model]'
      );

      if (!payload) {
        throw new Error('Cart validation data unavailable');
      }

      const model = parseModel(
        payload.dataset.builderCartModel
      );

      if (sequence === state.sequence) {
        state.model = model;

        document.dispatchEvent(
          new CustomEvent('on:builder-cart:updated', {
            bubbles: true,
            detail: { model }
          })
        );
      }

      return model;

    } catch (error) {
      if (sequence === state.sequence) {
        state.failed = true;
      }

      throw error;

    } finally {
      clearTimeout(timeout);
      state.refreshing--;
      renderAll();
    }
  }

  const notifyPending = () => {
    document.dispatchEvent(
      new CustomEvent('on:builder-cart:pending', {
        bubbles: true,
        detail: { pending: state.pending > 0 }
      })
    );

    renderAll();
  };

  window.ProductBuilderCartValidation = {
    refresh,

    hasPendingUpdates() {
      return state.pending > 0;
    },

    beginUpdate() {
      state.pending++;
      state.version++;
      notifyPending();
    },

    endUpdate() {
      state.pending = Math.max(0, state.pending - 1);
      state.version++;
      notifyPending();

      if (!state.pending && guards.size) {
        refresh().catch(() => {});
      }
    }
  };

  class ProductBuilderCartValidation extends HTMLElement {

    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.form) return;

        this.form = document.getElementById(
          this.dataset.formId
        );

        if (!this.form) return;

        this.submitHandler = (event) =>
          this.handleSubmit(event);

        this.form.addEventListener(
          'submit',
          this.submitHandler,
          true
        );

        guards.add(this);

        try {
          state.model = parseModel(
            this.dataset.builderCartModel
          );
          state.failed = false;
        } catch (error) {
          state.failed = true;
        }

        renderAll();
      });
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener(
          'submit',
          this.submitHandler,
          true
        );
      }

      this.form = null;
      guards.delete(this);
    }

    render() {
      if (!this.form) return;

      const model = state.model || {
        items: [],
        requirements: []
      };

      const missing = missingSteps(model);

      const busy =
        state.pending > 0 ||
        state.refreshing > 0 ||
        state.checking;

      const empty = isEmpty(model);

      const blocked =
        busy ||
        state.failed ||
        empty ||
        missing.length > 0;

      this.setAttribute('aria-busy', String(busy));

      Array.from(this.form.elements)
        .filter((element) => element.name === 'checkout')
        .forEach((button) => {
          button.disabled = blocked;
        });

      const summary =
        this.closest(
          '.cart-drawer__summary, .cart__summary'
        ) || this.form;

      summary
        .querySelectorAll('.dynamic-cart-btns')
        .forEach((container) => {
          container.hidden = blocked;
        });

      const errors = this.querySelector(
        '[data-builder-cart-errors]'
      );

      errors.replaceChildren();

      errors.hidden =
        !state.failed &&
        !empty &&
        !missing.length;

      if (state.failed) {
        const message = document.createElement('p');
        message.textContent = this.dataset.errorMessage;

        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'link';
        retry.textContent = this.dataset.retryText;

        retry.addEventListener('click', () => {
          refresh().catch(() => {});
        });

        errors.append(message, retry);

      } else if (empty) {
        const message = document.createElement('p');
        message.textContent = this.dataset.emptyMessage;

        errors.append(message);

      } else {
        // Group missing steps by machine.
        const byMachine = new Map();

        missing.forEach((step) => {
          const key = String(step.machineProductId);

          if (!byMachine.has(key)) {
            byMachine.set(key, {
              title: step.machineTitle,
              steps: []
            });
          }

          byMachine.get(key).steps.push(step.stepTitle);
        });

        byMachine.forEach((machine, machineId) => {
          const card = document.createElement('div');
          card.className = 'product-builder-cart__notice';

          const title = document.createElement('strong');
          title.className =
            'product-builder-cart__notice-title';
          title.textContent = 'Complete your bundle';

          const message = document.createElement('p');
          message.className =
            'product-builder-cart__notice-text';

          message.textContent =
            this.dataset.message.replace(
              '[machine]',
              machine.title
            );

          card.append(title, message);


        // Find the machine in the cart.
        const machineItem = model.items.find(
          (item) => String(item.product_id) === String(machineId)
        );

        if (machineItem) {
          try {
            const rawSize = machineItem.builder_size;
            const size = typeof rawSize === 'string'
              ? rawSize.trim()
              : '';

            let url;

            if (size) {
              // Open the Supplies collection filtered by machine size.
              url = new URL(
                '/collections/supplies',
                window.location.origin
              );

              url.searchParams.set('sort_by', 'manual');
              url.searchParams.set(
                'filter.p.m.custom.size',
                size
              );
            } else if (machineItem.product_url) {
              // Fallback if the machine has no Size metafield.
              url = new URL(
                machineItem.product_url,
                window.location.origin
              );
              url.hash = 'choose-supplies';
            }

            if (
              url &&
              url.origin === window.location.origin &&
              ['http:', 'https:'].includes(url.protocol)
            ) {
              const link = document.createElement('a');

              link.className =
                'btn btn--secondary product-builder-cart__notice-link';

              link.href = url.href;
              link.textContent = this.dataset.linkText;

              link.setAttribute(
                'aria-label',
                `${this.dataset.linkText} for ${machine.title}`
              );

              card.append(link);
            }
          } catch (error) {
            // Keep the warning if the URL is invalid.
          }
        }



          errors.append(card);
        });
      }
    }

    async handleSubmit(event) {
      if (event.submitter?.name !== 'checkout') return;

      if (this.allowSubmit) {
        this.allowSubmit = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (state.pending || state.checking) return;

      const form = this.form;
      const submitter = event.submitter;

      state.checking = true;
      renderAll();

      let ready = false;

      try {
        // Validating can start a lead-time save. Wait for it and validate the
        // saved cart, otherwise the shopper's click is lost.
        for (let attempt = 0; attempt < 3 && !ready; attempt++) {
          const version = state.version;
          const sequence = state.sequence + 1;
          const model = await refresh();

          ready =
            !state.pending &&
            !state.failed &&
            version === state.version &&
            sequence === state.sequence &&
            !isEmpty(model) &&
            !missingSteps(model).length &&
            !missingSteps(state.model).length;

          const leadTime = window.BuilderCartLeadTime;

          if (ready || !leadTime?.isBusy?.()) break;

          await leadTime.whenIdle();
        }

      } catch (error) {
        // Checkout stays blocked if validation fails.

      } finally {
        state.checking = false;
        renderAll();
      }

      if (
        ready &&
        this.isConnected &&
        this.form === form &&
        submitter.form === form
      ) {
        // An older refresh can still hold the button disabled. A disabled button
        // is left out of the form data, and Shopify would not go to checkout.
        submitter.disabled = false;

        this.allowSubmit = true;
        form.requestSubmit(submitter);
        this.allowSubmit = false;
      }
    }
  }

  customElements.define(
    'product-builder-cart-validation',
    ProductBuilderCartValidation
  );

  document.addEventListener('cart:updated', () => {
    state.version++;

    if (!state.pending && guards.size) {
      refresh().catch(() => {});
    }
  });

  document.addEventListener(
    'on:builder-cart:sections-rendered',
    () => queueMicrotask(renderAll)
  );
})();
